/**
 * CanvasPixelRenderer.ts — Canvas-based pixel art rendering engine.
 *
 * Renders PixelBuffer art on a canvas overlay positioned above the xterm
 * terminal. Each pixel in the buffer becomes a real square on the canvas,
 * giving crisp pixel art without the half-block character hacks.
 *
 * Uses the same PixelBuffer format as PixelArtRenderer, so all existing
 * art data in PixelArtData.ts works unchanged.
 *
 * The canvas is created once and reused. Art is drawn at terminal row/col
 * positions by converting cell coordinates to pixel coordinates using
 * xterm's actual cell dimensions.
 */

import type { Terminal } from './Terminal';
import { PALETTE } from './PixelArtPalette';
import type { PixelBuffer } from './PixelArtRenderer';

// Pre-build CSS color strings from palette
const PALETTE_COLORS: string[] = [];
for (const entry of PALETTE) {
    PALETTE_COLORS[entry.index] = `rgb(${entry.r},${entry.g},${entry.b})`;
}

export class CanvasPixelRenderer {
    private terminal: Terminal;
    private canvas: HTMLCanvasElement | null = null;
    private ctx: CanvasRenderingContext2D | null = null;
    private container: HTMLElement | null = null;

    /** Pixels per art pixel — controls rendered size */
    private scale = 3;

    constructor(terminal: Terminal) {
        this.terminal = terminal;
    }

    // ── Setup ──────────────────────────────────────────────────

    /** Attach to a container element (call once during boot) */
    attach(container: HTMLElement): void {
        this.container = container;

        this.canvas = document.createElement('canvas');
        this.canvas.style.cssText = [
            'position: absolute',
            'top: 0',
            'left: 0',
            'width: 100%',
            'height: 100%',
            'pointer-events: none',
            'z-index: 5',
            'display: none',
        ].join(';');

        this.ctx = this.canvas.getContext('2d')!;
        container.appendChild(this.canvas);

        // Keep canvas pixel dimensions in sync with container
        const ro = new ResizeObserver(() => this.syncSize());
        ro.observe(container);
    }

    private syncSize(): void {
        if (!this.canvas || !this.container) return;
        const rect = this.container.getBoundingClientRect();
        const dpr = window.devicePixelRatio || 1;
        this.canvas.width = Math.round(rect.width * dpr);
        this.canvas.height = Math.round(rect.height * dpr);
        this.ctx?.scale(dpr, dpr);
    }

    // ── Cell geometry ──────────────────────────────────────────

    /**
     * Get the pixel dimensions of a single xterm cell.
     * Uses the xterm internal renderer dimensions when available,
     * falls back to calculating from container size / cell count.
     */
    private getCellSize(): { cellW: number; cellH: number } {
        const raw = this.terminal.getRawTerminal() as any;

        // Try xterm internal dimensions (most accurate)
        const dims = raw._core?._renderService?.dimensions;
        if (dims?.css?.cell) {
            return { cellW: dims.css.cell.width, cellH: dims.css.cell.height };
        }

        // Fallback: derive from container and terminal grid
        const { cols, rows } = this.terminal.getSize();
        const rect = this.container?.getBoundingClientRect();
        if (rect && cols > 0 && rows > 0) {
            return { cellW: rect.width / cols, cellH: rect.height / rows };
        }

        // Last resort: estimate from font size
        const fontSize = this.terminal.getFontSize();
        return { cellW: fontSize * 0.6, cellH: fontSize * 1.2 };
    }

    /**
     * Convert terminal row/col (1-based) to canvas pixel coordinates.
     */
    private cellToPixel(row: number, col: number): { x: number; y: number } {
        const { cellW, cellH } = this.getCellSize();
        return {
            x: (col - 1) * cellW,
            y: (row - 1) * cellH,
        };
    }

    // ── Public API ─────────────────────────────────────────────

    /**
     * Render pixel art centered within a panel region, clipped to panel bounds.
     * Uses ctx.save()/ctx.clip()/ctx.restore() to prevent bleeding into adjacent panels.
     * Returns the number of terminal rows the art occupies.
     */
    renderInPanel(
        art: PixelBuffer,
        startRow: number,
        leftCol: number,
        panelWidth: number,
        maxRows?: number,
    ): number {
        if (!this.ctx || !this.canvas) return this.getCharHeight(art);

        const { cellW, cellH } = this.getCellSize();

        // Compute clip rect from panel bounds (1-based row/col to pixel coords)
        const clipX = (leftCol - 1) * cellW;
        const clipW = panelWidth * cellW;
        const clipY = (startRow - 1) * cellH;
        const clipH = maxRows
            ? maxRows * cellH
            : (this.canvas.height / (window.devicePixelRatio || 1)) - clipY;

        // Show the canvas and sync dimensions
        this.canvas.style.display = 'block';
        this.syncSize();

        const ctx = this.ctx;
        const dpr = window.devicePixelRatio || 1;

        // Reset transform to account for DPR scaling
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

        // Set clip rect to panel bounds
        ctx.save();
        ctx.beginPath();
        ctx.rect(clipX, clipY, clipW, clipH);
        ctx.clip();

        // Center art within panel
        const offset = Math.max(0, Math.floor((panelWidth - this.getCharWidth(art)) / 2));
        const origin = this.cellToPixel(startRow, leftCol + offset);
        const s = this.scale;

        for (let y = 0; y < art.height; y++) {
            for (let x = 0; x < art.width; x++) {
                const idx = art.pixels[y * art.width + x];
                if (idx === 0) continue; // transparent

                ctx.fillStyle = PALETTE_COLORS[idx];
                ctx.fillRect(
                    origin.x + x * s,
                    origin.y + y * s,
                    s,
                    s,
                );
            }
        }

        ctx.restore();
        return this.getCharHeight(art);
    }

    /**
     * Render pixel art at an absolute terminal position (1-based row/col).
     * Returns the number of terminal rows the art occupies.
     */
    renderAt(art: PixelBuffer, row: number, col: number): number {
        if (!this.canvas || !this.ctx) return this.getCharHeight(art);

        // Show the canvas
        this.canvas.style.display = 'block';

        // Ensure canvas dimensions are current
        this.syncSize();

        const ctx = this.ctx;
        const dpr = window.devicePixelRatio || 1;

        // Calculate pixel size: fit art within the cell region
        // Each art pixel gets `scale` real pixels
        const s = this.scale;

        // Top-left corner in CSS pixels
        const origin = this.cellToPixel(row, col);

        // Reset transform to account for DPR scaling we did in syncSize
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

        for (let y = 0; y < art.height; y++) {
            for (let x = 0; x < art.width; x++) {
                const idx = art.pixels[y * art.width + x];
                if (idx === 0) continue; // transparent

                ctx.fillStyle = PALETTE_COLORS[idx];
                ctx.fillRect(
                    origin.x + x * s,
                    origin.y + y * s,
                    s,
                    s,
                );
            }
        }

        return this.getCharHeight(art);
    }

    /**
     * Clear the canvas (call when transitioning scenes).
     */
    clear(): void {
        if (!this.canvas || !this.ctx) return;
        const dpr = window.devicePixelRatio || 1;
        this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        this.ctx.clearRect(0, 0, this.canvas.width / dpr, this.canvas.height / dpr);
    }

    /**
     * Hide the canvas overlay entirely.
     */
    hide(): void {
        this.clear();
        if (this.canvas) {
            this.canvas.style.display = 'none';
        }
    }

    /**
     * Number of terminal character rows the art occupies.
     * Each art pixel row takes `scale` real pixels; a terminal row is `cellH` pixels.
     */
    getCharHeight(art: PixelBuffer): number {
        const { cellH } = this.getCellSize();
        return Math.ceil((art.height * this.scale) / cellH);
    }

    /**
     * Number of terminal character columns the art occupies.
     */
    getCharWidth(art: PixelBuffer): number {
        const { cellW } = this.getCellSize();
        return Math.ceil((art.width * this.scale) / cellW);
    }

    /**
     * Check if pixel art fits within a panel's dimensions (in terminal cells).
     */
    fitsInPanel(art: PixelBuffer, panelWidth: number, panelHeight: number): boolean {
        return this.getCharWidth(art) <= panelWidth && this.getCharHeight(art) <= panelHeight;
    }

    /**
     * Set the pixel scale (pixels per art pixel). Default is 3.
     */
    setScale(s: number): void {
        this.scale = Math.max(1, Math.round(s));
    }

    /** Get current scale */
    getScale(): number {
        return this.scale;
    }
}
